import { type StorageInterface } from '@/common/storageInterface'
import { Logger } from '@/util'

const log = Logger.get('yb.storage.opfs')

const MiB = 1024 * 1024

export default class OPFSStorage implements StorageInterface {
  _userName: string
  _storageItemLimit: number

  constructor(userName: string) {
    this._userName = userName

    const storageItemLimitConfigMB = (useRuntimeConfig()?.public?.storage as any)?.browserItemLimitMB
    this._storageItemLimit = (storageItemLimitConfigMB || 10) * MiB // ~10 MiB max per value
  }

  private makeRootKey(rootKey: string): string {
    return `${rootKey}:${this._userName}`
  }

  private async getRoot(rootKey: string): Promise<FileSystemDirectoryHandle> {
    const root = await navigator.storage.getDirectory()
    return root.getDirectoryHandle(this.makeRootKey(rootKey), { create: true })
  }

  private async ensureDir(rootKey: string, pathParts: string[]): Promise<FileSystemDirectoryHandle> {
    let dir = await this.getRoot(rootKey)
    for (const part of pathParts) {
      dir = await dir.getDirectoryHandle(part, { create: true })
    }
    return dir
  }

  async readPath(rootKey: string, path: string): Promise<string|null> {
    const parts = path.split('/')
    const fileName = parts.pop()!
    const dir = await this.ensureDir(rootKey, parts)
    const fh = await dir.getFileHandle(fileName, { create: false }).catch(() => undefined)
    if (!fh) {
      return null
    }
    const file = await fh.getFile()
    return await file.text()
  }

  async writePath(rootKey: string, path: string, content: string): Promise<void> {
    if (content.length > this._storageItemLimit) {
      throw new Error(`Cannot store data item greater than ${this._storageItemLimit / MiB} MiB in size`)
    }
    try {
      const parts = path.split('/')
      const fileName = parts.pop()!
      const dir = await this.ensureDir(rootKey, parts)
      const fh = await dir.getFileHandle(fileName, { create: true })
      const ws = await fh.createWritable()
      await ws.write(content)
      await ws.close()
    } catch (e: any) {
      log.error(`Could not write storage ${rootKey}/${path}:`, e)
      throw e
    }
  }

  async removePath(rootKey: string, path: string | null | undefined): Promise<void> {
    try {
      const parts = path?.split('/').filter(Boolean) ?? []
      if (parts.length === 0) {
        // remove entire rootKey dir
        const root = await navigator.storage.getDirectory()
        await root.removeEntry(this.makeRootKey(rootKey), { recursive: true })
      } else {
        const leaf = parts.pop()!
        const dir = await this.ensureDir(rootKey, parts)
        await dir.removeEntry(leaf, { recursive: true })
      }
    } catch (e: any) {
      if (e?.name !== 'NotFoundError' && e?.name !== 'NoModificationAllowedError') {
        log.error(`Could not remove path ${rootKey}/${path}:`, e)
        throw e
      }
    }
  }

  async listPath(rootKey: string, path: string): Promise<string[]> {
    const parts = path.split('/').filter(Boolean)
    const dir = await this.ensureDir(rootKey, parts)
    const results: string[] = []
    for await (const [name] of dir.entries()) {
      results.push(name)
    }
    return results
  }

  async supported(): Promise<boolean> {
    if (!('storage' in navigator && 'getDirectory' in navigator.storage)) {
      return false
    }
    try {
      const root = await navigator.storage.getDirectory()
      const supportedEntry = 'opfs-supported'
      const fh = await root.getFileHandle(supportedEntry, { create: true })
      const supported = 'createWritable' in fh // safari doesn't have this, fallback
      root.removeEntry(supportedEntry)
      return supported
    } catch {
      return false // private browsing or blocked
    }
  }
}
